import { useState, type FormEvent } from 'react'
import { api, errorMessage } from '../api/client'
import type { Item } from '../api/types'
import { Card, Empty, Field, Note, NumberInput, PageHead } from '../components/ui'
import { useAuth } from '../auth/AuthContext'
import { useApi } from '../hooks/useApi'

export function Items() {
  const { isOwner } = useAuth()
  // Shares the entry every picker in the app is already using. Adding a flower
  // calls reload() below, which asks again regardless of how recent it is.
  const items = useApi<Item[]>('/items', [], { cache: true })

  const [name, setName] = useState('')
  const [rate, setRate] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function add(event: FormEvent) {
    event.preventDefault()
    setError(null)
    try {
      await api.post('/items', { name, defaultRate: rate || '0' })
      setName('')
      setRate('')
      items.reload()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  async function saveRate(item: Item, newRate: string) {
    if (item.defaultRate === newRate || !newRate) return
    try {
      await api.put(`/items/${item.id}`, { name: item.name, defaultRate: newRate })
      items.reload()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  async function remove(item: Item) {
    setError(null)
    try {
      await api.delete(`/items/${item.id}`)
      items.reload()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  return (
    <>
      <PageHead title="چیزیں" hint="پھولوں کی قسمیں اور اُن کا عام ریٹ۔" />

      {error && <Note kind="error">{error}</Note>}

      <Card title="نئی چیز">
        <form onSubmit={add} className="row">
          <Field label="نام">
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
          </Field>
          <Field label="عام ریٹ" hint="نئے سودے میں یہی ریٹ پہلے سے لکھا آ جائے گا۔">
            <NumberInput value={rate} onChange={setRate} placeholder="0" />
          </Field>
          <div className="field">
            <label>&nbsp;</label>
            <button className="btn" disabled={!name.trim()}>
              لکھیں
            </button>
          </div>
        </form>
      </Card>

      <Card title="لسٹ">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>چیز</th>
                <th>عام ریٹ</th>
                {isOwner && <th></th>}
              </tr>
            </thead>
            <tbody>
              {(items.data ?? []).map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td className="num">
                    <input
                      defaultValue={item.defaultRate}
                      onBlur={(e) => saveRate(item, e.target.value.trim())}
                      inputMode="numeric"
                      style={{
                        border: '1px solid transparent',
                        background: 'transparent',
                        padding: '4px 6px',
                        borderRadius: 6,
                        font: 'inherit',
                        direction: 'ltr',
                        textAlign: 'right',
                        width: 100,
                      }}
                    />
                  </td>
                  {isOwner && (
                    <td>
                      <button
                        type="button"
                        className="btn btn--danger btn--small"
                        onClick={() => remove(item)}
                      >
                        ڈیلیٹ
                      </button>
                    </td>
                  )}
                </tr>
              ))}

              {(items.data?.length ?? 0) === 0 && (
                <tr>
                  <td colSpan={3}>
                    <Empty>ابھی کوئی چیز نہیں لکھی۔</Empty>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {isOwner && (
          <p className="field__hint" style={{ marginTop: 10 }}>
            جس چیز کا کوئی سودا لکھا ہوا ہے وہ ہٹ نہیں سکتی، ورنہ پرانی انٹری پڑھی نہیں جا سکے گی۔
          </p>
        )}
        <p className="field__hint">
          عام ریٹ صرف فارم بھرنے میں مدد دیتا ہے۔ پرانے سودے اِس سے نہیں بدلتے — اُن میں وہی ریٹ رہتا
          ہے جو اُس دن لکھا گیا۔
        </p>
      </Card>
    </>
  )
}
